"use client";

import { useRef, type ReactNode } from "react";
import { gsap, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

type ParallaxElementProps = {
  children?: ReactNode;
  className?: string;
  /**
   * Travel as a fraction of the viewport height across the element's pass through the
   * viewport. Keep it small (0.03–0.15): depth should be felt, not noticed.
   * Negative values move against the scroll.
   */
  speed?: number;
  axis?: "x" | "y";
  /** Optional scale drift across the same range, e.g. [1.06, 1]. */
  scale?: [number, number];
  rotate?: [number, number];
};

export function ParallaxElement({
  children,
  className,
  speed = 0.08,
  axis = "y",
  scale,
  rotate,
}: ParallaxElementProps) {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || reduceMotion()) return;

      const mm = gsap.matchMedia();
      mm.add({ mobile: "(max-width: 767px)", desktop: "(min-width: 768px)" }, (ctx) => {
        const factor = ctx.conditions?.mobile ? 0.45 : 1;
        const distance = () => window.innerHeight * speed * factor;
        const from: gsap.TweenVars = { [axis]: () => -distance() };
        const to: gsap.TweenVars = { [axis]: () => distance() };
        if (scale) {
          from.scale = scale[0];
          to.scale = scale[1];
        }
        if (rotate) {
          from.rotate = rotate[0];
          to.rotate = rotate[1];
        }
        gsap.fromTo(el, from, {
          ...to,
          ease: "none",
          scrollTrigger: {
            trigger: el.parentElement ?? el,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,
          },
        });
      });
    },
    { scope: ref },
  );

  return (
    <div ref={ref} className={cn("will-change-transform", className)}>
      {children}
    </div>
  );
}
